
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const Hero = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  
  // Updated with luxury landscape images that better represent the services
  const slides = [
    {
      image: "https://images.unsplash.com/photo-1558293842-c0fd3db86157?q=80&w=2069&auto=format&fit=crop",
      heading: "Luxury Landscaping Services in Connecticut",
      subheading: "Transform your outdoor space into a masterpiece with PK Landscape Associates",
    },
    {
      image: "https://images.unsplash.com/photo-1553530979-fbb9e4aee36f?q=80&w=2070&auto=format&fit=crop",
      heading: "Premier Landscape Design & Installation",
      subheading: "Customized solutions for Fairfield County's most discerning property owners",
    },
    {
      image: "https://images.unsplash.com/photo-1555955924-a8c17aa846b7?q=80&w=2070&auto=format&fit=crop",
      heading: "Professional Maintenance Year-Round",
      subheading: "Preserving the beauty and value of your landscape investment",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 7000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const handleDotClick = (index: number) => {
    setActiveSlide(index);
  };

  const handleImageError = (src: string) => {
    console.error(`Failed to load image: ${src}`);
    setImageError(prev => ({...prev, [src]: true}));
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Hero Image Slider */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={cn(
            "absolute inset-0 w-full h-full transition-opacity duration-1000 ease-smooth",
            activeSlide === index ? "opacity-100" : "opacity-0"
          )}
        >
          {/* Slightly darker overlay for better text contrast */}
          <div className="absolute inset-0 bg-black opacity-65 z-10"></div>
          {imageError[slide.image] ? (
            <div className="absolute inset-0 bg-pkgreen-900 flex items-center justify-center z-5">
              <span className="text-white text-lg">Loading image...</span>
            </div>
          ) : (
            <img
              src={slide.image}
              alt={slide.heading}
              className="absolute inset-0 w-full h-full object-cover animate-image-pan"
              onError={() => handleImageError(slide.image)}
            />
          )}
        </div>
      ))}

      {/* Hero Content */}
      <div className="absolute inset-0 flex items-center justify-center px-4 z-20">
        <div className="container mx-auto max-w-6xl">
          <div className="max-w-3xl px-4 py-12 relative">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={cn(
                  "transition-all duration-1000 ease-smooth absolute",
                  activeSlide === index
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
              >
                <span className="block text-pkgreen-400 text-lg md:text-xl mb-4 font-display uppercase tracking-wider animate-fade-up font-semibold">
                  Excellence in Landscape Design
                </span>
                <h1 className="hero-text text-white mb-6 leading-tight animate-fade-up text-shadow text-4xl md:text-5xl lg:text-6xl font-serif font-bold">
                  {slide.heading}
                </h1>
                <p className="text-white text-lg md:text-xl mb-8 max-w-2xl animate-fade-up font-medium text-shadow">
                  {slide.subheading}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-up">
                  <Link
                    to="/contact"
                    className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white py-3 px-8 rounded-md inline-flex items-center transition-all duration-300 ease-smooth"
                  >
                    Request a Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    to="/portfolio"
                    className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white py-3 px-8 rounded-md inline-flex items-center transition-all duration-300 ease-smooth"
                  >
                    View Our Portfolio
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slider Controls */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20">
        <div className="flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={cn(
                "w-2.5 h-2.5 rounded-full transition-all duration-300 ease-smooth",
                activeSlide === index
                  ? "bg-white w-8"
                  : "bg-white/50 hover:bg-white/80"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block z-20">
        <div className="w-0.5 h-16 bg-white/50 mx-auto"></div>
      </div>

      {/* Image loading error messages - only shown in development */}
      {Object.keys(imageError).length > 0 && (
        <div className="absolute bottom-4 left-4 bg-red-500 text-white p-2 text-sm rounded z-50">
          Failed to load {Object.keys(imageError).length} image(s). Check console for details.
        </div>
      )}
    </section>
  );
};

export default Hero;
